import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wordmark } from './Wordmark';
import Icon from './Icon';
import LangSwitch from './LangSwitch';
import { APP_URL } from '../../config';
import useLocalizedPath from '../../hooks/useLocalizedPath';

export default function Nav({ current = 'home' }) {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const links = [
    { id: 'home',      label: t('nav.home'),      to: lp('/') },
    { id: 'pixviewer', label: t('nav.pixviewer'), to: lp('/pixviewer') },
    { id: 'pixrow',    label: t('nav.pixrow'),    to: lp('/pixrow') },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? 'rgba(5,7,10,0.82)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px) saturate(140%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(12px) saturate(140%)' : 'none',
        borderBottom: scrolled ? '1px solid var(--p2a-dark-border)' : '1px solid transparent',
        transition: 'background var(--p2a-dur-base) var(--p2a-ease), border-color var(--p2a-dur-base) var(--p2a-ease)',
      }}>
        <div className="p2-container" style={{
          height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Link to={lp('/')} style={{ display: 'flex', alignItems: 'center' }}>
            <Wordmark size={26} />
          </Link>

          <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="nav-desktop">
            {links.map((l) => (
              <Link
                key={l.id}
                to={l.to}
                style={{
                  padding: '8px 14px',
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: current === l.id ? 'var(--p2a-dark-fg-1)' : 'var(--p2a-dark-fg-3)',
                  letterSpacing: '-0.005em',
                  position: 'relative',
                  transition: 'color var(--p2a-dur-base) var(--p2a-ease)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--p2a-green-400)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = current === l.id ? 'var(--p2a-dark-fg-1)' : 'var(--p2a-dark-fg-3)')}
              >
                {l.label}
                {current === l.id && (
                  <span style={{
                    position: 'absolute', left: 14, right: 14, bottom: 2,
                    height: 1, background: 'var(--p2a-green-500)',
                  }} />
                )}
              </Link>
            ))}
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }} className="nav-actions">
            <LangSwitch />
            {/* Ghost, e não primário: o visitante desconhecido é a maioria
                aqui e é quem o "Fale conosco" precisa capturar. Quem já é
                cliente sabe o que procura e acha um botão secundário. */}
            <a
              href={APP_URL}
              className="btn btn-ghost nav-app-cta"
              style={{ padding: '8px 14px', fontSize: 13 }}
            >
              {t('nav.app_cta')}
            </a>
            <a
              href="https://api.whatsapp.com/send?phone=5519996542014&text=Ol%C3%A1%2C%20gostaria%20de%20falar%20com%20a%20equipe%20da%20Pix2Agro."
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary nav-contact-cta"
              style={{ padding: '8px 14px', fontSize: 13 }}
            >
              {t('nav.contact_cta')}
            </a>
            <button
              aria-label={t('nav.menu')}
              className="nav-mobile-btn"
              onClick={() => setOpen((o) => !o)}
              style={{
                display: 'none',
                width: 40, height: 40,
                flexShrink: 0,
                border: '1px solid var(--p2a-dark-border-2)', borderRadius: 4,
                color: 'var(--p2a-dark-fg-1)',
                alignItems: 'center', justifyContent: 'center',
              }}
            >
              {open ? <Icon.Close width={18} height={18} /> : <Icon.Menu width={18} height={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="nav-mobile-panel" style={{
            borderTop: '1px solid var(--p2a-dark-border)',
            background: 'rgba(5,7,10,0.96)',
            backdropFilter: 'blur(12px)',
          }}>
            <div className="p2-container" style={{ padding: '16px 32px' }}>
              {links.map((l) => (
                <Link
                  key={l.id}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  style={{
                    display: 'block', padding: '12px 0',
                    borderBottom: '1px solid var(--p2a-dark-border)',
                    fontSize: 15,
                    color: current === l.id ? 'var(--p2a-green-400)' : 'var(--p2a-dark-fg-1)',
                  }}
                >
                  {l.label}
                </Link>
              ))}
              {/* Os dois CTAs precisam existir aqui: o desktop esconde
                  `.nav-app-cta` e `.nav-contact-cta` abaixo de 720px, e sem
                  isto o cliente no celular — que é justamente quem está no
                  campo — ficaria sem entrada para o produto. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 16 }}>
                <a
                  href={APP_URL}
                  onClick={() => setOpen(false)}
                  className="btn btn-primary"
                  style={{ justifyContent: 'center' }}
                >
                  {t('nav.app_cta')}
                </a>
                <a
                  href="https://api.whatsapp.com/send?phone=5519996542014&text=Ol%C3%A1%2C%20gostaria%20de%20falar%20com%20a%20equipe%20da%20Pix2Agro."
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="btn btn-ghost"
                  style={{ justifyContent: 'center' }}
                >
                  {t('nav.contact_cta')}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 820px) {
          .nav-desktop { display: none !important; }
          .nav-actions .btn-ghost { display: none !important; }
          .nav-mobile-btn { display: inline-flex !important; }
        }
        @media (max-width: 720px) {
          .nav-contact-cta, .nav-app-cta { display: none !important; }
        }
      `}</style>
    </>
  );
}
